/**
 * Commission model shared by the broker RFP forms and BTX.
 *
 * A commission is a type plus an optional detail. The detail lives in `amount`
 * for percentage, PEPM, and graded, and in `heapedAmount` for heaped; the
 * remaining types carry no detail at all. This mirrors the canonical
 * `RfpCommissions` shape (`currentType` / `currentAmount` /
 * `currentHeapedAmount`) so both sides map to the wire without translation.
 */

import {
  CommissionTypeEnum,
  CurrentCommissionTypeEnum,
  extendEnumWithNotDisclosed,
  NotDisclosed,
} from "@platform/openapi-types";

import { formatPercent, SENTINEL_LABELS } from "./locs/shared";

/** Percentages are bounded at 100 wherever they appear. */
export const PERCENT_MAX = 100;

/** PEPM is a dollar amount capped at $100. */
export const PEPM_MAX = 100;

/** Fraction digits a PEPM amount is formatted to, since it is a currency. */
export const COMMISSION_DECIMAL_SCALE = 2;

/**
 * Heaped commission as the API carries it: first year, then thereafter.
 */
export type HeapedAmount = [first: number, thereafter: number];

/**
 * A commission selection and its detail.
 *
 * `amount` is the percentage for `percentage`, the dollar figure for `pepm`,
 * and the highest percentage for `graded`. A `graded` commission with a null
 * amount is the "No defined percentage" case — the mode is derived from the
 * amount rather than stored alongside it.
 */
export interface CommissionDetail {
  type?: string;
  amount?: number | null;
  heapedAmount?: HeapedAmount | null;
}

/**
 * A commission the broker arranges outside the carrier, which BTX has no
 * member for: the analyst decides what it becomes there. Offered on the RFP
 * form only, so it never reaches a BTX dropdown or the commission table.
 */
export const BrokerStandard = "brokerStandard" as const;

/** The order both surfaces present commission types in. */
const COMMISSION_TYPE_ORDER: readonly string[] = [
  CurrentCommissionTypeEnum.percentage,
  CurrentCommissionTypeEnum.net,
  CurrentCommissionTypeEnum.graded,
  CurrentCommissionTypeEnum.heaped,
  CurrentCommissionTypeEnum.pepm,
  CurrentCommissionTypeEnum.standard,
  BrokerStandard,
  CurrentCommissionTypeEnum.notApplicable,
  NotDisclosed,
];

const inPresentationOrder = (types: readonly string[]): string[] =>
  COMMISSION_TYPE_ORDER.filter((type) => types.includes(type));

/**
 * Current commission options for the analyst's grid.
 *
 * Keeps every type the commission table can hold: the grid both displays rows
 * BTX wrote itself and lets the analyst correct them, so dropping a value here
 * would strand the rows already carrying it.
 */
export const CURRENT_COMMISSION_OPTIONS: string[] = inPresentationOrder(
  extendEnumWithNotDisclosed(CurrentCommissionTypeEnum),
);

/**
 * Requested commission options.
 *
 * Narrower than the current-commission list: a requested commission cannot be
 * "Not Applicable" or "Not Disclosed", and canonical `CommissionTypeEnum` has
 * no member for either.
 */
export const REQUESTED_COMMISSION_OPTIONS: string[] = inPresentationOrder(
  Object.values(CommissionTypeEnum),
);

/**
 * Current commission options as the RFP form offers them.
 *
 * A broker states how they are paid; "Carrier Standard" and "Not Applicable"
 * are conclusions only an analyst can reach, and "Broker Standard" is a value
 * only a broker can mean.
 */
export const RFP_CURRENT_COMMISSION_OPTIONS: string[] = inPresentationOrder([
  CurrentCommissionTypeEnum.percentage,
  CurrentCommissionTypeEnum.net,
  CurrentCommissionTypeEnum.graded,
  CurrentCommissionTypeEnum.heaped,
  CurrentCommissionTypeEnum.pepm,
  BrokerStandard,
  NotDisclosed,
]);

/** Requested commission options: the current list without "Unknown". */
export const RFP_REQUESTED_COMMISSION_OPTIONS: string[] =
  RFP_CURRENT_COMMISSION_OPTIONS.filter((type) => type !== NotDisclosed);

export const COMMISSION_TYPE_LABELS: Record<string, string> = {
  [CurrentCommissionTypeEnum.heaped]: "Heaped",
  [CurrentCommissionTypeEnum.percentage]: "Flat Percentage",
  [CurrentCommissionTypeEnum.standard]: "Carrier Standard",
  [CurrentCommissionTypeEnum.graded]: "Graded",
  [CurrentCommissionTypeEnum.net]: "Net",
  [CurrentCommissionTypeEnum.pepm]: "PEPM",
  [CurrentCommissionTypeEnum.notApplicable]:
    SENTINEL_LABELS[CurrentCommissionTypeEnum.notApplicable],
  // "Unknown" only where a commission is being read; the other benefit fields
  // keep the shared sentinel wording.
  [NotDisclosed]: "Unknown",
  [BrokerStandard]: "Broker Standard",
};

/** Types that are complete on selection and show no detail input. */
const TYPES_WITHOUT_DETAIL: readonly string[] = [
  CurrentCommissionTypeEnum.standard,
  CurrentCommissionTypeEnum.net,
  CurrentCommissionTypeEnum.notApplicable,
  NotDisclosed,
  BrokerStandard,
];

export const commissionTypeLabel = (type?: string): string =>
  type ? (COMMISSION_TYPE_LABELS[type] ?? type) : "";

/** Whether selecting this type is complete on its own. */
export const shouldOmitAmount = (type?: string): boolean =>
  !type || TYPES_WITHOUT_DETAIL.includes(type);

/** Types whose detail input has to be filled in. */
const TYPES_REQUIRING_AMOUNT: readonly string[] = [
  CurrentCommissionTypeEnum.percentage,
  CurrentCommissionTypeEnum.pepm,
];

/**
 * Whether a type's detail is complete.
 *
 * Percentage and PEPM need their number. Heaped and graded take their detail
 * when the broker has it and stand on the type alone when they do not.
 */
export const commissionDetailIsComplete = (
  detail?: CommissionDetail | null,
): boolean =>
  !TYPES_REQUIRING_AMOUNT.includes(detail?.type ?? "") ||
  typeof detail?.amount === "number";

/** The upper bound for a type's amount, or undefined when it takes none. */
export const commissionAmountMax = (type?: string): number | undefined => {
  if (type === CurrentCommissionTypeEnum.pepm) {
    return PEPM_MAX;
  }

  if (
    type === CurrentCommissionTypeEnum.percentage ||
    type === CurrentCommissionTypeEnum.graded
  ) {
    return PERCENT_MAX;
  }

  return undefined;
};

/**
 * Converts the editor's `{ first, thereafter }` pair to the API's array form.
 * Returns null when either half is missing, since the array has no way to
 * express a half-filled heaped commission.
 */
export const heapedToPair = (
  value?: { first?: number | null; thereafter?: number | null } | null,
): HeapedAmount | null => {
  if (!value) {
    return null;
  }

  const { first, thereafter } = value;

  return typeof first === "number" && typeof thereafter === "number"
    ? [first, thereafter]
    : null;
};

/** Converts the API's array form back to the editor's `{ first, thereafter }`. */
export const pairToHeaped = (
  value?: HeapedAmount | number[] | null,
): { first: number; thereafter: number } | null => {
  if (!value) {
    return null;
  }

  const [first, thereafter] = value;

  return typeof first === "number" && typeof thereafter === "number"
    ? { first, thereafter }
    : null;
};

const pepmCurrency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: COMMISSION_DECIMAL_SCALE,
  maximumFractionDigits: COMMISSION_DECIMAL_SCALE,
});

export const formatPepmAmount = (amount: number): string =>
  pepmCurrency.format(amount);

/**
 * Renders a commission for display and for the legacy free-text field the
 * broker portal still writes alongside the discrete values.
 *
 * A type with a missing or inapplicable detail renders as the type label
 * alone, which is what the grid and the summary already show for those cases.
 */
export const formatCommission = (detail?: CommissionDetail | null): string => {
  if (!detail?.type) {
    return "";
  }

  const label = commissionTypeLabel(detail.type);

  if (shouldOmitAmount(detail.type)) {
    return label;
  }

  if (detail.type === CurrentCommissionTypeEnum.heaped) {
    const heaped = pairToHeaped(detail.heapedAmount);

    return heaped
      ? `${label} ${formatPercent(heaped.first)}/${formatPercent(heaped.thereafter)}`
      : label;
  }

  if (typeof detail.amount !== "number") {
    return label;
  }

  if (detail.type === CurrentCommissionTypeEnum.pepm) {
    return `${label} ${formatPepmAmount(detail.amount)}`;
  }

  if (detail.type === CurrentCommissionTypeEnum.graded) {
    return `${label} ${formatPercent(detail.amount)}`;
  }

  return formatPercent(detail.amount);
};
